'use client';

import { useEffect, useRef, useState } from 'react';
import { pinterestPinIntent, socialShareIntents } from '@containd/shared';
import {
  IconFacebook,
  IconLink,
  IconLinkedIn,
  IconPinterest,
  IconShare,
  IconWhatsApp,
  IconX,
} from './SocialShareIcons';
import { usePublicUi } from '@/lib/publicUi';
import styles from './SocialShareBar.module.css';

export type SocialShareBarProps = {
  url: string;
  title: string;
  description?: string;
  imageUrl?: string | null;
  /** Private/unlisted pages: copy only, no public network intents. */
  copyOnly?: boolean;
  /** When set, show an "Embed" option that copies this iframe snippet. */
  embedCode?: string | null;
};

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(el);
      return ok;
    } catch {
      return false;
    }
  }
}

export function SocialShareBar({
  url,
  title,
  description,
  imageUrl,
  copyOnly = false,
  embedCode = null,
}: SocialShareBarProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [canNative, setCanNative] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { extra } = usePublicUi();

  useEffect(() => {
    setCanNative(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (ev: MouseEvent) => {
      if (!rootRef.current?.contains(ev.target as Node)) setOpen(false);
    };
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const intents = socialShareIntents({
    url,
    title,
    description,
    imageUrl,
  });

  const copy = async () => {
    const ok = await writeClipboard(url);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 1800);
  };

  const copyEmbed = async () => {
    if (!embedCode) return;
    const ok = await writeClipboard(embedCode);
    setEmbedCopied(ok);
    if (ok) setTimeout(() => setEmbedCopied(false), 1800);
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title, text: description || title, url });
      setOpen(false);
    } catch {
      // user cancelled
    }
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={extra.shareLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <IconShare size={15} />
        <span className={styles.triggerLabel}>{extra.shareLabel}</span>
      </button>
      {open ? (
        <div className={styles.panel} role="menu" aria-label={extra.shareThisPage}>
          {canNative ? (
            <button type="button" className={styles.row} onClick={() => void nativeShare()}>
              <IconShare size={15} />
              {extra.shareViaDevice}
            </button>
          ) : null}
          {copyOnly ? null : (
            <>
              <a
                className={styles.row}
                href={intents.x}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
              >
                <IconX size={15} />
                X
              </a>
              <a
                className={styles.row}
                href={intents.facebook}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
              >
                <IconFacebook size={15} />
                Facebook
              </a>
              <a
                className={styles.row}
                href={intents.pinterest}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
              >
                <IconPinterest size={15} />
                Pinterest
              </a>
              <a
                className={styles.row}
                href={intents.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
              >
                <IconWhatsApp size={15} />
                WhatsApp
              </a>
              <a
                className={styles.row}
                href={intents.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                role="menuitem"
              >
                <IconLinkedIn size={15} />
                LinkedIn
              </a>
            </>
          )}
          <button type="button" className={styles.row} onClick={() => void copy()}>
            <IconLink size={15} />
            {copied ? extra.shareCopied : extra.shareCopyLink}
          </button>
          {embedCode ? (
            <>
              <button
                type="button"
                className={styles.row}
                aria-expanded={showEmbed}
                onClick={() => setShowEmbed((v) => !v)}
              >
                <IconLink size={15} />
                Embed
              </button>
              {showEmbed ? (
                <div style={{ padding: '0 0.5rem 0.5rem' }}>
                  <textarea
                    readOnly
                    rows={3}
                    value={embedCode}
                    onFocus={(e) => e.currentTarget.select()}
                    style={{
                      width: '100%',
                      boxSizing: 'border-box',
                      fontFamily: 'monospace',
                      fontSize: '0.72rem',
                      lineHeight: 1.35,
                      resize: 'vertical',
                    }}
                  />
                  <button
                    type="button"
                    className={styles.row}
                    onClick={() => void copyEmbed()}
                  >
                    <IconLink size={15} />
                    {embedCopied ? extra.shareCopied : 'Copy embed code'}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function PinterestPinButton({
  pageUrl,
  mediaUrl,
  description,
  className,
}: {
  pageUrl: string;
  mediaUrl: string;
  description: string;
  className?: string;
}) {
  const href = pinterestPinIntent({
    pageUrl,
    mediaUrl,
    description,
  });
  const { extra } = usePublicUi();
  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={extra.sharePinPinterest}
      aria-label={extra.sharePinPinterest}
      onClick={(e) => e.stopPropagation()}
    >
      <IconPinterest size={14} />
    </a>
  );
}
